<template>
    <div class="app-tree">
        <div class="top" v-if='needSearch'>
            <slot name="treeTop"></slot>
            <el-input class="input-height" suffix-icon="el-icon-search"
                      clearable :placeholder="placeholder" v-model="keywords"/>
        </div>
        <el-tree
            class="pb-3"
            ref="tree"
            :data="treeData"
            :props="defaultProps"
            :show-checkbox='showCheckbox'
            :node-key="defaultProps.key"
            :filter-node-method="filterNode"
            :expand-on-click-node="expandOnClickNode"
            :default-expanded-keys="defaultExpandedKeys"
            :highlight-current="true"
            @node-click="handleNodeClick">
            <template slot-scope="{ node, data }">
                <span class="custom-tree-node"
                      :title="node.label"
                      :class="(data[defaultProps['isLeaf']] || (!data[defaultProps['children']] || !data[defaultProps['children']].length)) ? 'isLeaf' : ''">
                     <slot :nodeData='data'>
                         {{ node.label }}
                     </slot>
                </span>
            </template>
        </el-tree>
    </div>
</template>

<script>

export default {
    name: 'app-tree',
    props: {
        needTotalLabel: {
            type: Boolean,
            default: false
        },
        notNeedCodeList: {
            type: Array,
            default: () => {
                return [];
            }
        },
        // 点击父节点时是否展开
        expandOnClickNode: {
            type: Boolean,
            default: false
        },
        showCheckbox: {
            type: Boolean,
            default: false
        },
        leafEnable: Boolean,
        placeholder: {
            type: String,
            default: '搜索目录名'
        },
        needSearch: {
            type: Boolean,
            default: true
        },
        defaultProps: {
            type: Object,
            default: () => {
                return {
                    children: 'children',
                    label: 'label',
                    key: 'code'
                };
            }
        },
        // API模块+方法名 如：PatrolManage.getOpsTree 中间点号（ . ）连接
        serviceName: String,
        list: {
            type: Array,
            default: () => {
                return [];
            }
        },
        needSearchKey: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            keywords: '',
            treeData: [],
            defaultCheckedKeys: [], // 默认勾选
            defaultExpandedKeys: [], // 默认展开
            currentNode: {}
        };
    },
    watch: {
        keywords (val) {
            this.$refs.tree.filter(val);
        },
        serviceName () {
            this.getTree();
        },
        list: {
            handler (val) {
                this.getTree();
            },
            deep: true
        }
    },
    created () {
        this.getTree();
    },
    methods: {
        getTree () {
            if (this.serviceName) {
                this.renderTreeByServer();
            } else {
                this.renderTreeByData();
            }
        },
        // 通过传入的数据进行渲染
        renderTreeByData () {
            this.formatData(this.list);
        },
        // 通过传入的服务名请求数据进行渲染
        renderTreeByServer () {
            // 服务名配置有误
            if (this.serviceName.indexOf('.') === -1) {
                return;
            }
            let serviceNameList = this.serviceName.split('.');
            let apiName = serviceNameList[0];
            let axiosName = serviceNameList[1];
            this.$api[apiName][axiosName]()
                .then(res => {
                    this.formatData(res);
                }).catch((err) => {
                    console.error(err);
                });
        },
        formatData (res) {
            if (!res || !res.length) {
                this.treeData = [];
                return;
            }
            let list = res;
            if (this.needTotalLabel) {
                let obj = {};
                obj[this.defaultProps.children] = list;
                obj[this.defaultProps.label] = '全部';
                obj[this.defaultProps.key] = '';
                this.treeData = [obj];
            } else {
                this.treeData = list || [];
            }
            this.initTreeActive();
            // 抛出获取的树数据
            this.$emit('setTreeData', this.treeData);
        },
        // 筛选方法
        filterNode (value, data) {
            if (!value) return true;
            // 不区分大小写的匹配
            let label = data[this.defaultProps.label] + '';
            label = label.toLowerCase();
            value = value.toLowerCase();
            if (this.needSearchKey) {
                let key = data[this.defaultProps.key] + '';
                key = key.toLowerCase();
                return label.indexOf(value) !== -1 || key.indexOf(value) !== -1;
            } else {
                return label.indexOf(value) !== -1;
            }
        },
        // 递归找出第一个叶子结点
        getTreeLeaf (treeData) {
            if (!treeData[this.defaultProps.children] || treeData[this.defaultProps.children].length === 0) {
                return treeData;
            }
            return this.getTreeLeaf(treeData[this.defaultProps.children][0]);
        },
        // 目录树默认选中
        initTreeActive () {
            if (!this.treeData.length) {
                return;
            }
            let firstNode = this.treeData[0];
            let firstLeafNode = firstNode;
            if (this.leafEnable) {
                firstLeafNode = this.getTreeLeaf(firstNode);
            }
            let idFirst = firstLeafNode[this.defaultProps.key];
            this.defaultExpandedKeys = [idFirst];
            this.$nextTick(() => {
                this.$refs.tree.setCurrentKey(idFirst);
            });
            this.currentNode = firstLeafNode;
            this.$emit('node-click', firstLeafNode);
        },
        setCurrentKey (keys) {
            this.$nextTick(() => {
                this.$refs.tree.setCurrentKey(keys);
            });
        },
        handleNodeClick (data) {
            if (this.leafEnable) {
                if (!data[this.defaultProps.children] || data[this.defaultProps.children].length === 0) {
                    this.currentNode = data;
                    this.$emit('node-click', data);
                } else {
                    // 父级节点不选中，仅展开伸缩
                    this.$refs.tree.setCurrentKey(this.currentNode[this.defaultProps.key]);
                }
                return;
            }
            this.currentNode = data;
            this.$emit('node-click', data);
        }
    }
};
</script>

<style lang="scss" scoped>
.app-tree {
    height: 100%;
    display: flex;
    flex-direction: column;
    .el-tree {
        flex: 1 1  auto;
        overflow-y: scroll;
    }
    .top {
        padding: 16px;
    }
    ::v-deep {
        .el-input__inner {
            background-color: var(--knowledge-right-bg);
            color: var(--knowledge-text);
            border-color: var(--border-color);
        }
        .el-input__inner:hover, .el-input__inner:focus {
            border-color: var(--border-color-hover);
        }
        .is-current > .el-tree-node__content {
            background-color: rgba(24, 144, 255, 0.1) !important;
            color: rgba(24, 144, 255, 1);
            border-right-color: rgba(24, 144, 255, 1);
        }
        .el-tree-node__content:hover {
            background-color: rgba(24, 144, 255, 0.05);
        }
        .el-tree-node > .el-tree-node__children {
            overflow: visible;
        }
        .el-tree-node__content {
            width: 100%;
            height: 32px;
            padding-right: 3px;
            border-right: 3px solid transparent;
            box-sizing: border-box;
            .custom-tree-node {
                width: 100%;
                height: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                display: inline-block;
                vertical-align: bottom;
                padding-right: 10px;
            }
        }
        .el-tree-node__expand-icon:not(.el-tree-node__expand-icon.is-leaf) {
            color: var(--font-color65);
            margin-left: 10px;
        }
    }
}
</style>
